import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles, Target, Eye, Heart, Shield, Zap, Users, Award, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { aboutApi, sectionApi, aboutPageContentApi, aboutExtendedApi, assetUrl } from '../services/api';

type AboutItem = {
  id: number;
  title?: string;
  subtitle?: string;
  description?: string;
  overview_content?: string;
  overview_image?: string | null;
  image_url?: string | null;
  is_active?: boolean;
  display_order?: number;
  heading_color?: string;
  text_color?: string;
  bg_color?: string;
  gradient_start?: string;
  gradient_end?: string;
  features?: any;
};

type MissionVisionData = {
  mission_title?: string;
  mission_description?: string;
  vision_title?: string;
  vision_description?: string;
};

type CoreValue = {
  id?: number;
  title?: string;
  description?: string;
  icon?: string;
  display_order?: number;
};

type WhyChooseItem = {
  id?: number;
  title?: string;
  description?: string;
  icon?: string;
  display_order?: number;
};

const DEMO_ITEMS: AboutItem[] = [
  {
    id: 9001,
    title: 'Welcome to Soltechy',
    subtitle: 'About Us',
    description: 'We are a leading technology company dedicated to delivering innovative digital solutions.',
    overview_content: '<p>Soltechy is a premier technology company specializing in cutting-edge web development, mobile applications, and digital transformation solutions. With years of experience and a team of expert developers, designers, and strategists, we help businesses thrive in the digital age.</p>',
    overview_image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1800&auto=format&fit=crop',
    is_active: true,
    display_order: 1,
    gradient_start: '#FFBF00',
    gradient_end: '#0b0c15',
  },
];

const DEMO_MISSION_VISION: MissionVisionData = {
  mission_title: 'Our Mission',
  mission_description: 'To empower businesses with innovative technology solutions that drive growth, efficiency, and success in the digital landscape.',
  vision_title: 'Our Vision',
  vision_description: 'To be the global leader in digital transformation, recognized for excellence, innovation, and customer-centric solutions that shape the future of technology.',
};

const DEMO_CORE_VALUES: CoreValue[] = [
  { id: 1, title: 'Innovation', description: 'Constantly pushing boundaries and embracing new technologies.', icon: 'Zap', display_order: 1 },
  { id: 2, title: 'Integrity', description: 'Building trust through transparency and ethical practices.', icon: 'Shield', display_order: 2 },
  { id: 3, title: 'Excellence', description: 'Delivering quality in every aspect of our work.', icon: 'Award', display_order: 3 },
  { id: 4, title: 'Collaboration', description: 'Working together to achieve extraordinary results.', icon: 'Users', display_order: 4 },
];

const DEMO_WHY_CHOOSE: WhyChooseItem[] = [
  { id: 1, title: 'Expert Team', description: 'Highly skilled professionals with years of industry experience.', icon: 'Users', display_order: 1 },
  { id: 2, title: 'Custom Solutions', description: 'Tailored solutions designed to meet your specific business needs.', icon: 'Target', display_order: 2 },
  { id: 3, title: '24/7 Support', description: 'Round-the-clock support to ensure your business never stops.', icon: 'Clock', display_order: 3 },
  { id: 4, title: 'Quality Assurance', description: 'Rigorous testing and quality control processes.', icon: 'CheckCircle', display_order: 4 },
  { id: 5, title: 'Competitive Pricing', description: 'Cost-effective solutions without compromising on quality.', icon: 'TrendingUp', display_order: 5 },
  { id: 6, title: 'Proven Track Record', description: 'Successful delivery of numerous projects across industries.', icon: 'Award', display_order: 6 },
];

const iconMap: Record<string, any> = {
  Target, Eye, Heart, Shield, Zap, Users, Award, TrendingUp, Clock, CheckCircle,
};

const getIcon = (iconName?: string) => {
  return iconMap[iconName || ''] || Target;
};

const AboutPage: React.FC = () => {
  const [items, setItems] = useState<AboutItem[]>([]);
  const [heading, setHeading] = useState('About Us');
  const [description, setDescription] = useState('');
  const [ctaHeading, setCtaHeading] = useState('Let\'s Build Something Great');
  const [ctaDescription, setCtaDescription] = useState('Ready to transform your business?');
  const [ctaButtonText, setCtaButtonText] = useState('Start a Project');
  const [ctaButtonAction, setCtaButtonAction] = useState<'modal' | 'link'>('modal');
  const [ctaButtonLink, setCtaButtonLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [missionVision, setMissionVision] = useState<MissionVisionData>(DEMO_MISSION_VISION);
  const [coreValues, setCoreValues] = useState<CoreValue[]>(DEMO_CORE_VALUES);
  const [whyChoose, setWhyChoose] = useState<WhyChooseItem[]>(DEMO_WHY_CHOOSE);

  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, -50]);
  const y2 = useTransform(scrollY, [0, 500], [0, 50]);

  const sortedItems = [...items]
    .filter((item) => item.is_active !== false)
    .sort((a, b) => Number(a.display_order ?? 0) - Number(b.display_order ?? 0));
  const renderedItems = sortedItems.length > 0 ? sortedItems : DEMO_ITEMS;

  useEffect(() => {
    window.scrollTo(0, 0);
    const load = async () => {
      try {
        const [aboutRes, headingRes, aboutPageContentRes, missionVisionRes, coreValuesRes, whyChooseRes] = await Promise.all([
          aboutApi.getAll(),
          sectionApi.get('about'),
          aboutPageContentApi.get(),
          aboutExtendedApi.getMissionVision().catch(() => ({ success: false, data: null })),
          aboutExtendedApi.getCoreValues().catch(() => ({ success: false, data: null })),
          aboutExtendedApi.getWhyChoose().catch(() => ({ success: false, data: null })),
        ]);

        if (headingRes?.success && headingRes.data) {
          const sectionData = headingRes.data as any;
          setHeading(String(sectionData.heading || 'About Us'));
          setDescription(String(sectionData.description || ''));
        }

        if (aboutPageContentRes?.success && aboutPageContentRes.data) {
          const ctaData = aboutPageContentRes.data as any;
          setCtaHeading(String(ctaData.cta_heading || 'Let\'s Build Something Great'));
          setCtaDescription(String(ctaData.cta_description || 'Ready to transform your business?'));
          setCtaButtonText(String(ctaData.button_text || 'Start a Project'));
          setCtaButtonAction(ctaData.button_action === 'link' ? 'link' : 'modal');
          setCtaButtonLink(String(ctaData.button_link || ''));
        }

        if (missionVisionRes?.success && missionVisionRes.data) {
          setMissionVision(missionVisionRes.data as MissionVisionData);
        }

        if (coreValuesRes?.success && coreValuesRes.data && Array.isArray(coreValuesRes.data.values)) {
          setCoreValues(coreValuesRes.data.values as CoreValue[]);
        }

        if (whyChooseRes?.success && whyChooseRes.data && Array.isArray(whyChooseRes.data.items)) {
          setWhyChoose(whyChooseRes.data.items as WhyChooseItem[]);
        }

        if (aboutRes?.success && Array.isArray(aboutRes.data)) {
          setItems(aboutRes.data as AboutItem[]);
        } else {
          setItems([]);
        }
      } catch (error) {
        console.error('Failed to load about page data:', error);
        // Fallback to demo data on error
        setItems(DEMO_ITEMS);
        setMissionVision(DEMO_MISSION_VISION);
        setCoreValues(DEMO_CORE_VALUES);
        setWhyChoose(DEMO_WHY_CHOOSE);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const sortedCoreValues = [...coreValues].sort((a, b) => Number(a.display_order ?? 0) - Number(b.display_order ?? 0));
  const sortedWhyChoose = [...whyChoose].sort((a, b) => Number(a.display_order ?? 0) - Number(b.display_order ?? 0));

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <motion.div style={{ y: y1 }} className="absolute top-[-10%] left-[-10%] h-[50vw] w-[50vw] rounded-full bg-amber-600/10 blur-[120px]" />
        <motion.div style={{ y: y2 }} className="absolute bottom-[-10%] right-[-10%] h-[50vw] w-[50vw] rounded-full bg-purple-600/10 blur-[120px]" />
        <div className="absolute inset-0 opacity-5">
          <div
            className="h-full w-full"
            style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Ccircle cx=\'7\' cy=\'7\' r=\'7\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}
          />
        </div>
      </div>

      <div className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center px-4 md:px-6 pt-32">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-black leading-[0.95] tracking-tight mb-6">
                {heading}
              </h1>
              {description && (
                <div
                  className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
            </motion.div>

            {/* About Section with Image */}
            {renderedItems.map((item, index) => {
              const visual = item.overview_image || item.image_url || null;
              const contentHtml = item.overview_content || item.description || '';

              return (
                <motion.section
                  key={item.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-32"
                >
                  <div className={`${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                      className="relative rounded-3xl overflow-hidden border border-white/10 bg-white/[0.03] shadow-2xl"
                    >
                      {visual ? (
                        <img src={assetUrl(visual)} alt={item.title || 'About visual'} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-[400px] flex items-center justify-center text-white/40">
                          Upload image from dashboard
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    </motion.div>
                  </div>

                  <div className={`${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                    <motion.div
                      initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                    >
                      <div className="inline-flex items-center gap-2 text-amber-400 text-xs uppercase tracking-[0.25em] mb-4">
                        {item.subtitle || 'About'}
                      </div>
                      <h2 className="text-4xl md:text-5xl font-black leading-tight mb-6" style={{ color: item.heading_color || '#ffffff' }}>
                        {item.title || 'About Soltechy'}
                      </h2>
                      <div className="prose prose-invert max-w-none text-white/80 text-lg" style={{ color: item.text_color || '#d1d5db' }}>
                        <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
                      </div>
                    </motion.div>
                  </div>
                </motion.section>
              );
            })}
          </div>
        </section>

        {/* Mission & Vision Section */}
        <section className="py-24 px-4 md:px-6">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
                Our <span className="text-amber-400">Mission</span> & <span className="text-amber-400">Vision</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Mission */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-transparent p-8 md:p-12 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-3xl" />
                <Target className="w-12 h-12 text-amber-400 mb-6" />
                <h3 className="text-2xl md:text-3xl font-black mb-4">{missionVision.mission_title || 'Our Mission'}</h3>
                <p className="text-white/80 text-lg leading-relaxed">{missionVision.mission_description || 'To empower businesses with innovative technology solutions.'}</p>
              </motion.div>

              {/* Vision */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-transparent p-8 md:p-12 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-3xl" />
                <Eye className="w-12 h-12 text-amber-400 mb-6" />
                <h3 className="text-2xl md:text-3xl font-black mb-4">{missionVision.vision_title || 'Our Vision'}</h3>
                <p className="text-white/80 text-lg leading-relaxed">{missionVision.vision_description || 'To be the global leader in digital transformation.'}</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="py-24 px-4 md:px-6 bg-white/[0.02]">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
                Our <span className="text-amber-400">Core Values</span>
              </h2>
              <p className="text-white/60 text-lg max-w-2xl mx-auto">
                The principles that guide everything we do
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {sortedCoreValues.map((value, index) => {
                const IconComponent = getIcon(value.icon);
                return (
                  <motion.div
                    key={value.id || index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -10, scale: 1.02 }}
                    className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-amber-500/50 transition-all duration-300"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-500/0 group-hover:from-amber-500/10 group-hover:to-transparent transition-all duration-300 rounded-2xl" />
                    <IconComponent className="w-10 h-10 text-amber-400 mb-4" />
                    <h3 className="text-xl font-bold mb-2">{value.title || 'Value'}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{value.description || 'Description'}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-24 px-4 md:px-6">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
                Why Choose <span className="text-amber-400">SOLTECHY</span> Technologies?
              </h2>
              <p className="text-white/60 text-lg max-w-2xl mx-auto">
                What sets us apart from the competition
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedWhyChoose.map((item, index) => {
                const IconComponent = getIcon(item.icon);
                return (
                  <motion.div
                    key={item.id || index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -8, scale: 1.03 }}
                    className="group relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.02] p-6 hover:border-amber-500/50 transition-all duration-300"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-500/0 group-hover:from-amber-500/10 group-hover:to-amber-500/10 transition-all duration-300 rounded-2xl" />
                    <div className="relative z-10">
                      <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4 group-hover:bg-amber-500/30 transition-colors">
                        <IconComponent className="w-7 h-7 text-amber-400" />
                      </div>
                      <h3 className="text-xl font-bold mb-2">{item.title || 'Feature'}</h3>
                      <p className="text-white/60 text-sm leading-relaxed">{item.description || 'Description'}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 md:px-6">
          <div className="container mx-auto max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/20 to-amber-600/10 p-8 md:p-16 text-center overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-64 h-64 bg-amber-500/30 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-amber-600/30 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <h3 className="text-3xl md:text-5xl font-black tracking-tight mb-6">{ctaHeading}</h3>
                <div
                  className="text-white/70 text-lg max-w-2xl mx-auto mb-8"
                  dangerouslySetInnerHTML={{ __html: ctaDescription }}
                />
                {ctaButtonAction === 'link' && ctaButtonLink ? (
                  <a
                    href={ctaButtonLink}
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold hover:from-amber-400 hover:to-amber-500 transition-all transform hover:scale-105 shadow-lg shadow-amber-500/25"
                  >
                    {ctaButtonText}
                    <ArrowRight size={18} />
                  </a>
                ) : (
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold hover:from-amber-400 hover:to-amber-500 transition-all transform hover:scale-105 shadow-lg shadow-amber-500/25"
                  >
                    {ctaButtonText}
                    <ArrowRight size={18} />
                  </Link>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
